import { Feature } from '@/pinia/feature'

export const humidity: Feature = {
  name: 'humidity',
  enabled: true,
  title: {
    en: 'Humidity',
    sv: 'Luftfuktighet'
  },
  info: {
    en: 'Humidity is collected as averages over 10-minute intervals and aggregated over longer periods of time, then represented in percent relative humidity (%).',
    sv: 'Luftfuktigheten hämtas som medelvärden över 10-minutersintervall och aggregeras över längre tidsperioder för att sedan representeras i procent relativ luftfuktighet (%)'
  },
  component: 'DashboardDataSectionFlat',
  isRoute: true,
  props: {
    dataSources: [
      {
        id: 'climate.humidity.kpi.0',
        type: 'fetch',
        fetch: 'fetch.indoorclimate.humidity.kpi',
        path: 'kpis'
      },
      {
        id: 'climate.humidity.chart',
        type: 'fetch',
        fetch: 'fetch.indoorclimate.humidity.timeseries',
        path: 'timeSeries[0]'
      },
      {
        id: 'climate.humidity.histogram',
        type: 'fetch',
        fetch: 'fetch.indoorclimate.humidity.histogram',
        path: 'histograms[0].range'
      },
      {
        id: 'climate.humidity.toplist.average',
        type: 'fetch',
        fetch: 'fetch.indoorclimate.humidity.toplist.average',
        path: ''
      },
      {
        id: 'climate.humidity.colorscale',
        type: 'value',
        value: {
          type: 'INTERPOLATE',
          cutoffs: [
            {
              value: 0,
              color: '#FF4D4F'
            },
            {
              value: 30,
              color: '#FFA940'
            },
            {
              value: 45,
              color: '#FFA940'
            },
            {
              value: 60,
              color: '#73D13D'
            },
            {
              value: 75,
              color: '#36CFC9'
            },
            {
              value: 80,
              color: '#0BA5EC'
            },
            {
              value: 100,
              color: '#0155AC'
            }
          ]
        }
      }
    ]
  },
  children: [
    {
      name: 'humidityKPICard',
      enabled: true,
      title: {
        en: 'Mean value',
        sv: 'Medelvärde'
      },
      info: {
        en: 'Average over the selected time interval and chosen rooms.',
        sv: 'Medelvärde över valt tidsintervall och valda rum.'
      },
      component: 'KPICard',
      props: {
        colorScale: 'climate.humidity.colorscale',
        dataSource: 'climate.humidity.kpi.0',
        isLoading: false,
        showOnMap: false,
        showOnMapEnabled: true,
        first: true,
        kpiFormat: {
          format: {
            type: 'intl-number',
            maximumFractionDigits: 1
          },
          steps: [{ format: { type: 'format-string', format: '%s%' } }]
        },
        metadataFormat: {
          space: {}
        },
        metadataLabels: {
          space: {
            en: 'Number of spaces',
            sv: 'Antal rum'
          }
        }
      }
    },
    {
      name: 'divider',
      title: {
        en: 'Divider',
        sv: 'Avdelare'
      },
      component: 'DividerComponent',
      enabled: true,
      props: {
        width: 'auto',
        fullscreenWidth: '70%'
      }
    },
    {
      name: 'humidity',
      enabled: true,
      title: {
        en: 'Change over time',
        sv: 'Förändring över tid'
      },
      info: {
        en: 'The graph visualizes the average indoor relative humidity over a given time period. The curve represents the varying levels of humidity and provides insight into how indoor humidity changes over time. This information is valuable for understanding and optimizing comfort levels, the health of the building, and indoor well-being.  \n\nThe gray areas in the graph indicate the absence of available data for certain periods or data that has been deliberately filtered out by the user (for example, weekends).',
        sv: 'Grafen visualiserar den relativa luftfuktighetens medelvärde inomhus över en given tidsperiod. Kurvan representerar de varierande nivåerna av luftfuktighet och ger insikt i hur fuktigheten inomhus förändras över tid. Denna information är värdefull för att förstå och optimera komfortnivåer, husets hälsa och välbefinnande inomhus.  \n\nDe gråa områdena i grafen markerar avsaknaden av tillgänglig data under vissa tidsperioder eller data som medvetet har filtrerats bort av användaren (till exempel helger).'
      },
      component: 'CardChart',
      props: {
        enableFullscreen: true,
        chart: {
          type: 'timeseries',
          dataFormat: {
            format: {
              type: 'intl-number',
              maximumFractionDigits: 1
            },
            steps: [{ format: { type: 'format-string', format: '%s%' } }]
          },
          colorScale: 'climate.humidity.colorscale',
          labels: [
            {
              sv: 'Luftfuktighet',
              en: 'Humidity'
            }
          ],
          lineFormats: [
            {
              style: 'solid'
            }
          ],
          dataSources: ['climate.humidity.chart'],
          missingDataMessage: {
            en: 'Missing data',
            sv: 'Saknar data'
          }
        }
      }
    },
    {
      name: 'humidity-hist',
      enabled: true,
      title: {
        en: 'Dispersion',
        sv: 'Spridning'
      },
      info: {
        en: 'This bar chart provides an overview of the indoor humidity distribution over a given time period.  \n\nEach bar represents different humidity intervals, and its height indicates the frequency of observations within each range. By analyzing the bar chart, one gets a clear picture of the distribution of humidity, which can be crucial for optimizing indoor comfort and health.',
        sv: 'Detta stapeldiagram ger en översikt över spridningen av luftfuktighet inomhus under en given tidsperiod.  \n\nVarje stapel representerar olika luftfuktighetsintervall och dess höjd indikerar frekvensen av observationer inom varje intervall. Genom att analysera stapeldiagrammet får man en tydlig bild av fördelningen av luftfuktighet, vilket kan vara avgörande för att optimera inomhuskomfort och hälsa.'
      },
      component: 'CardChart',
      props: {
        enableFullscreen: true,
        chart: {
          type: 'histogram',
          codomainFormat: {
            format: {
              type: 'intl-number',
              style: 'percent',
              maximumFractionDigits: 1
            }
          },
          domainFormat: {
            format: {
              type: 'intl-number',
              maximumFractionDigits: 1
            },
            steps: [{ format: { type: 'format-string', format: '%s%' } }]
          },
          valueFilterTreshold: 0.001,
          codomainTicks: {
            values: [0, 0.5, 1]
          },
          missingDataMessage: {
            en: 'Missing data',
            sv: 'Saknar data'
          },
          label: {
            sv: 'Luftfuktighet',
            en: 'Humidity'
          },
          colorScale: 'climate.humidity.colorscale',
          dataSource: 'climate.humidity.histogram'
        }
      }
    },
    {
      name: 'index-toplist',
      enabled: true,
      title: {
        en: 'Toplist',
        sv: 'Topplista'
      },
      component: 'CardTopList',
      props: {
        dataSource: 'climate.humidity.toplist.average',
        info: 'Some info about the toplist',
        format: {
          format: {
            type: 'intl-number',
            maximumFractionDigits: 1
          },
          steps: [{ format: { type: 'format-string', format: '%s%' } }]
        },
        barOptions: [
          {
            label: {
              en: 'Humidity',
              sv: 'Luftfuktighet'
            },
            color: '#0BA5EC',
            gradient: true,
            tooltip: true
          },
          {
            label: {
              en: 'Not measured',
              sv: 'Ej mätt'
            },
            color: '#E0E0E0',
            gradient: true,
            tooltip: false
          }
        ],
        errorMessages: {
          NO_DATA: {
            en: 'No data available',
            sv: 'Ingen data tillgänglig'
          },
          INVALID_FILTER: {
            en: 'Filter on complex to show data',
            sv: 'Filtrera på komplex för att visa data'
          }
        }
      }
    }
  ]
}
